import { format } from "date-fns"
import { CreditCard, MapPin, MousePointerClick, Phone, Receipt, Route } from "lucide-react"
import type { ReactNode } from "react"
import { TelegramHtml } from "@/entities/bot-builder"
import { MEDIA_KIND_LABELS } from "@/shared/config/media"
import { cn } from "@/shared/lib/utils"
import type { AudienceEvent } from "../api/audience-api"
import { mapUrl } from "../lib/map-url"
import { MemberFile, rubles } from "./audience-ui"

const KIND_LABELS = MEDIA_KIND_LABELS as Record<string, string>

const formatTime = (value: string) => format(new Date(value), "HH:mm")

function field(data: AudienceEvent["data"], key: string): string | undefined {
	const value = data?.[key]
	return typeof value === "string" || typeof value === "number" ? String(value) : undefined
}

/** Одно событие истории: пользователь справа, бот слева, как в тест-чате. */
export function EventView({
	event,
	botId,
	sessionId,
}: {
	event: AudienceEvent
	botId: string
	sessionId: string
}) {
	const incoming = event.direction === "IN"
	const fromBotmaker = event.data?.source === "botmaker"
	let body: ReactNode
	switch (event.type) {
		case "imported_flow":
			body = (
				<span className="inline-flex items-center gap-1.5 text-muted-foreground">
					<Route className="h-3.5 w-3.5 opacity-70" />
					{event.text}
				</span>
			)
			break
		case "button":
			body = (
				<span className="inline-flex items-center gap-1.5">
					<MousePointerClick className="h-3.5 w-3.5 opacity-70" />
					{event.text}
				</span>
			)
			break
		case "contact":
			body = (
				<span className="inline-flex items-center gap-1.5">
					<Phone className="h-3.5 w-3.5 opacity-70" />
					{event.text}
				</span>
			)
			break
		case "location": {
			const latitude = field(event.data, "latitude")
			const longitude = field(event.data, "longitude")
			body =
				latitude && longitude ? (
					<a
						href={mapUrl(latitude, longitude)}
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-1.5 underline-offset-2 hover:underline"
					>
						<MapPin className="h-3.5 w-3.5 opacity-70" />
						Геолокация
					</a>
				) : (
					event.text
				)
			break
		}
		case "media": {
			const fileId = field(event.data, "fileId")
			body = (
				<div className="space-y-1.5">
					{fileId ? (
						<MemberFile
							botId={botId}
							sessionId={sessionId}
							fileId={fileId}
							kind={field(event.data, "kind") ?? "document"}
							fileName={field(event.data, "fileName")}
						/>
					) : null}
					{event.text ? <p className="break-words whitespace-pre-wrap">{event.text}</p> : null}
				</div>
			)
			break
		}
		case "payment":
			body = (
				<span className="inline-flex items-center gap-1.5 text-emerald-300">
					<CreditCard className="h-3.5 w-3.5" />
					Оплатил {event.text}
				</span>
			)
			break
		case "invoice": {
			const amount = Number(event.data?.amount)
			body = (
				<span className="inline-flex items-center gap-1.5">
					<Receipt className="h-3.5 w-3.5 opacity-70" />
					Счёт «{event.text}»{Number.isFinite(amount) ? ` на ${rubles(amount)}` : ""}
				</span>
			)
			break
		}
		case "message": {
			const media = Array.isArray(event.data?.media)
				? (event.data.media as { kind?: string; fileName?: string }[])
				: []
			const buttons = Array.isArray(event.data?.buttons) ? (event.data.buttons as string[]) : []
			body = (
				<div className="space-y-1.5">
					{media.length > 0 ? (
						<p className="text-xs text-muted-foreground">
							{media.map((item) => KIND_LABELS[item.kind ?? ""] ?? "Файл").join(", ")}
						</p>
					) : null}
					{event.text ? (
						<div className="break-words whitespace-pre-wrap">
							<TelegramHtml html={event.text} />
						</div>
					) : null}
					{buttons.length > 0 ? (
						<div className="flex flex-wrap gap-1 pt-0.5">
							{buttons.map((label, index) => (
								<span
									key={`${label}-${index}`}
									className="rounded-md border border-border/60 px-1.5 py-0.5 text-[11px] text-muted-foreground"
								>
									{label}
								</span>
							))}
						</div>
					) : null}
				</div>
			)
			break
		}
		default:
			body = <span className="break-words whitespace-pre-wrap">{event.text}</span>
	}

	return (
		<div className={cn("flex", incoming ? "justify-end" : "justify-start")}>
			<div
				className={cn(
					"max-w-[85%] rounded-2xl px-3 py-2 text-sm",
					incoming ? "rounded-br-md bg-primary/15" : "rounded-bl-md bg-muted/60",
				)}
			>
				{body}
				<p className="mt-1 text-right text-[10px] text-muted-foreground">
					{fromBotmaker ? "Botmaker · " : ""}
					{formatTime(event.createdAt)}
				</p>
			</div>
		</div>
	)
}
